import React, { useState, useRef, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import styles from './MenuUploadForm-Page.module.css';
import { startMenuJobAPI } from '../../context-api/api-services/MenuUpload.service';

function isValidFile(file) {
    if (!file) return false;
    return (
        file.type.startsWith('image/') ||
        file.type === 'application/pdf' ||
        file.name.toLowerCase().endsWith('.pdf')
    );
}

function formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const FORM_STATE = { IDLE: 'idle', SUBMITTING: 'submitting', SUBMITTED: 'submitted' };

const MenuUploadFormPage = () => {
    const navigate = useNavigate();
    const { setSelectedMenuTab } = useContext(GlobalStateDataContext);

    const goToJobsList = useCallback(() => {
        setSelectedMenuTab('menuupload');
        navigate('/menuuploadjobs');
    }, [navigate, setSelectedMenuTab]);

    const [kitchenName, setKitchenName] = useState('');
    const [uploadFile, setUploadFile] = useState(null);
    const [merchantEmail, setMerchantEmail] = useState('Arslan@singlecombo.com');
    const [adminEmail, setAdminEmail] = useState('Arslan@singlecombo.com');
    const [isDragOver, setIsDragOver] = useState(false);
    const [formState, setFormState] = useState(FORM_STATE.IDLE);
    const [errorMsg, setErrorMsg] = useState('');
    const [submittedJobId, setSubmittedJobId] = useState('');

    const fileInputRef = useRef(null);
    const isSubmitting = formState === FORM_STATE.SUBMITTING;
    const isDevelopmentMode = process.env.NODE_ENV === 'development';
    const isSubmitDisabled = (
        !isDevelopmentMode
        || isSubmitting
        || !uploadFile
        || !kitchenName.trim()
        || !merchantEmail.trim()
        || !adminEmail.trim()
    );

    const handleFileSelect = useCallback((file) => {
        if (!file) return;
        if (!isValidFile(file)) {
            setErrorMsg('Only image and PDF files are accepted (PNG, JPG, WEBP, PDF).');
            setUploadFile(null);
            return;
        }
        setErrorMsg('');
        setUploadFile(file);
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!uploadFile) { setErrorMsg('Please select a menu file.'); return; }
        if (!kitchenName.trim()) { setErrorMsg('Kitchen name is required.'); return; }
        if (!merchantEmail.trim()) { setErrorMsg('Merchant email is required.'); return; }
        if (!adminEmail.trim()) { setErrorMsg('Admin email is required.'); return; }

        setErrorMsg('');
        setFormState(FORM_STATE.SUBMITTING);

        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('merchant_email', merchantEmail.trim());
        formData.append('admin_email', adminEmail.trim());
        formData.append('kitchen_name', kitchenName.trim());

        const response = await startMenuJobAPI(formData);
        console.log('response startMenuJobAPI', response);
        if (response.hasError) {
            if (response.status === 409) {
                const suffix = response.existing_job_id
                    ? ` View existing job: ${response.existing_job_id}`
                    : '';
                setErrorMsg((response.message || 'A job is already running for this kitchen.') + suffix);
            } else if (response.status === 502) {
                const suffix = response.job_id
                    ? ` Job ID: ${response.job_id} (check the jobs list).`
                    : '';
                setErrorMsg((response.message || 'Failed to notify workflow.') + suffix);
            } else {
                setErrorMsg(response.message || 'Upload failed. Please try again.');
            }
            setFormState(FORM_STATE.IDLE);
            return;
        }
        const resolvedJobId = response.data?.job_id;
        if (!resolvedJobId) {
            setErrorMsg('Server did not return a job id.');
            setFormState(FORM_STATE.IDLE);
            return;
        }

        setSubmittedJobId(resolvedJobId);
        setFormState(FORM_STATE.SUBMITTED);
    }, [adminEmail, kitchenName, merchantEmail, uploadFile]);

    const handleReset = useCallback(() => {
        setUploadFile(null);
        setKitchenName('');
        setMerchantEmail('info@singlecombo.com');
        setAdminEmail('');
        setErrorMsg('');
        setSubmittedJobId('');
        setFormState(FORM_STATE.IDLE);
    }, []);

    if (formState === FORM_STATE.SUBMITTED) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    <div className={styles.card}>
                        <div className={styles.successState}>
                            <div className={styles.successIcon}>✓</div>
                            <h2 className={styles.successTitle}>Menu submitted!</h2>
                            <p className={styles.successSub}>
                                Your menu file has been received. AI processing has started for{' '}
                                <strong>{kitchenName.trim()}</strong>.
                            </p>
                            {submittedJobId && (
                                <p className={styles.jobIdBadge}>Job ID: {submittedJobId}</p>
                            )}
                            <div className={styles.successActions}>
                                <button
                                    type='button'
                                    className={styles.primaryBtn}
                                    onClick={goToJobsList}
                                >
                                    View Job Progress →
                                </button>
                                <button type='button' className={styles.secondaryBtn} onClick={handleReset}>
                                    Upload another menu
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <span className={styles.badge}>AI Menu Processor</span>
                    <h1 className={styles.title}>Upload Restaurant Menu</h1>
                    <p className={styles.subtitle}>
                        Upload your menu file and our AI will automatically extract, analyse, and process all dishes.
                    </p>
                    <button type='button' className={styles.jobsLink} onClick={goToJobsList}>
                        View active jobs →
                    </button>
                </div>

                <div className={styles.card}>
                    <form onSubmit={handleSubmit} noValidate>

                        <div className={styles.fieldGroup}>
                            <label className={styles.label} htmlFor='kitchen-name'>Kitchen name</label>
                            <input
                                id='kitchen-name'
                                type='text'
                                className={styles.input}
                                value={kitchenName}
                                onChange={(e) => setKitchenName(e.target.value)}
                                placeholder='e.g. Downtown Kitchen or your kitchen identifier'
                                disabled={isSubmitting}
                                autoComplete='organization'
                                required
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Menu File</label>
                            <div
                                className={[
                                    styles.dropZone,
                                    isDragOver ? styles.dropZoneActive : '',
                                    isSubmitting ? styles.dropZoneDisabled : '',
                                ].join(' ')}
                                role='button'
                                tabIndex={isSubmitting ? -1 : 0}
                                onClick={() => !isSubmitting && fileInputRef.current?.click()}
                                onKeyDown={(e) => {
                                    if (isSubmitting) return;
                                    if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
                                }}
                                onDragOver={(e) => { e.preventDefault(); if (!isSubmitting) setIsDragOver(true); }}
                                onDragLeave={() => setIsDragOver(false)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setIsDragOver(false);
                                    if (!isSubmitting) handleFileSelect(e.dataTransfer.files?.[0] || null);
                                }}
                            >
                                <input
                                    ref={fileInputRef}
                                    type='file'
                                    accept='image/*,.pdf'
                                    className={styles.hiddenInput}
                                    onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                                    disabled={isSubmitting}
                                />
                                {uploadFile ? (
                                    <div className={styles.fileChip}>
                                        <span className={styles.fileChipIcon}>📄</span>
                                        <div className={styles.fileChipBody}>
                                            <p className={styles.fileChipName}>{uploadFile.name}</p>
                                            <p className={styles.fileChipSize}>{formatBytes(uploadFile.size)}</p>
                                        </div>
                                        <button
                                            type='button'
                                            className={styles.fileChipRemove}
                                            aria-label='Remove file'
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setUploadFile(null);
                                            }}
                                            disabled={isSubmitting}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <div className={styles.dropPrompt}>
                                        <span className={styles.dropIcon} aria-hidden>⬆</span>
                                        <p className={styles.dropText}>Drag & drop your menu file here</p>
                                        <p className={styles.dropHint}>or click to browse · PNG, JPG, WEBP, PDF</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={styles.divider} />

                        <div className={styles.twoCol}>
                            <div className={styles.fieldGroup}>
                                <label className={styles.label} htmlFor='merchant-email'>Merchant Email</label>
                                <input
                                    id='merchant-email'
                                    type='email'
                                    className={styles.input}
                                    value={merchantEmail}
                                    onChange={(e) => setMerchantEmail(e.target.value)}
                                    placeholder='merchant@domain.com'
                                    disabled={isSubmitting}
                                    required
                                />
                            </div>
                            <div className={styles.fieldGroup}>
                                <label className={styles.label} htmlFor='admin-email'>Admin Email</label>
                                <input
                                    id='admin-email'
                                    type='email'
                                    className={styles.input}
                                    value={adminEmail}
                                    onChange={(e) => setAdminEmail(e.target.value)}
                                    placeholder='admin@domain.com'
                                    disabled={isSubmitting}
                                    required
                                />
                            </div>
                        </div>

                        {errorMsg && (
                            <div className={styles.errorMsg} role='alert'>
                                {errorMsg}
                            </div>
                        )}

                        <Tooltip
                            title='This feature is only available in production.'
                            placement='top'
                            arrow
                            disableHoverListener={!isDevelopmentMode}
                        >
                            <span className={styles.submitBtnWrap}>
                                <button
                                    type='submit'
                                    className={styles.submitBtn}
                                    disabled={isSubmitDisabled}
                                >
                                    {isSubmitting ? (
                                        <span className={styles.submitSpinner}>
                                            <span className={styles.spinnerDot} />
                                            <span className={styles.spinnerDot} />
                                            <span className={styles.spinnerDot} />
                                            Uploading…
                                        </span>
                                    ) : (
                                        'Upload & Process Menu'
                                    )}
                                </button>
                            </span>
                        </Tooltip>
                    </form>
                </div>

                <p className={styles.footNote}>
                    Supports PDF menus and photos · Typical processing time: 2–5 minutes
                </p>
            </div>
        </div>
    );
};

export default MenuUploadFormPage;
